import { useState } from 'react';
import { TextInput, PasswordInput, Checkbox } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconCheck,
  IconUserPlus,
  IconMail,
  IconLock,
} from '@tabler/icons-react';
import EmailPassword from 'supertokens-auth-react/recipe/emailpassword';
import { useNavigate } from 'react-router-dom';
import { clientRoutes } from '@/ApiPaths';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';
import {
  AuthPageShell,
  AuthSubmitButton,
  AuthSecondaryButton,
  AuthPrimaryButton,
} from '@/components/auth/AuthPageShell';
import { getAuthInputStyles } from '@/components/auth/authInputStyles';
import authClasses from '@/components/auth/AuthPage.module.css';

interface SignupFormData {
  email: string;
  password: string;
  confirmPassword: string;
  termsAndConditions: boolean;
  dataProcessing: boolean;
  newsletterSignup: boolean;
}

export function Signup() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const accentColor = DEFAULT_QR_CODE_COLOR;
  const inputStyles = getAuthInputStyles(accentColor);

  const form = useForm<SignupFormData>({
    initialValues: {
      email: '',
      password: '',
      confirmPassword: '',
      termsAndConditions: false,
      dataProcessing: false,
      newsletterSignup: false,
    },
    validate: {
      email: (value) =>
        /^\S+@\S+\.\S+$/.test(value) ? null : t('login.invalidEmail'),
      password: (value) =>
        value.length >= 6 ? null : t('login.passwordMinLength'),
      confirmPassword: (value, values) =>
        value !== values.password ? t('signup.passwordMismatch') : null,
      termsAndConditions: (value) => (!value ? t('signup.termsRequired') : null),
      dataProcessing: (value) => (!value ? t('signup.dataProcessingRequired') : null),
    },
  });

  const handleSubmit = async (values: SignupFormData) => {
    setIsSubmitting(true);

    try {
      const response = await EmailPassword.signUp({
        formFields: [
          { id: 'email', value: values.email },
          { id: 'password', value: values.password },
        ],
      });

      if (response.status === 'OK') {
        if (values.newsletterSignup) {
          // Boolean only — never persist the email (shared-device / XSS PII).
          // localStorage: email verification usually opens a new tab, which has empty sessionStorage.
          localStorage.setItem('newsletterOptIn', '1');
        } else {
          localStorage.removeItem('newsletterOptIn');
        }
        sessionStorage.removeItem('newsletterOptIn');
        // Clear legacy keys from older clients
        localStorage.removeItem('newsletterSignup');
        localStorage.removeItem('newsletterEmail');

        setSignupSuccess(true);
        notifications.show({
          title: t('signup.signupSuccess'),
          message: '',
          color: 'green',
          icon: <IconCheck size={16} />,
        });

        setTimeout(() => {
          navigate(clientRoutes.login);
        }, 2000);
        return;
      }

      if (response.status === 'FIELD_ERROR' && response.formFields) {
        const emailField = response.formFields.find((field) => field.id === 'email');
        if (
          emailField?.error &&
          (emailField.error.includes('already exists') ||
            emailField.error.includes('EMAIL_ALREADY_EXISTS_ERROR'))
        ) {
          throw new Error('EMAIL_ALREADY_EXISTS_ERROR');
        }
      }

      throw new Error(response.status);
    } catch (error: unknown) {
      sessionStorage.removeItem('newsletterOptIn');
      localStorage.removeItem('newsletterOptIn');
      localStorage.removeItem('newsletterSignup');
      localStorage.removeItem('newsletterEmail');

      let errorMessage = t('signup.signupError');
      const message = error instanceof Error ? error.message : '';

      if (
        message.includes('EMAIL_ALREADY_EXISTS_ERROR') ||
        message.includes('EMAIL_ALREADY_EXISTS')
      ) {
        errorMessage = t('signup.emailAlreadyExists');
      } else if (message.includes('FIELD_ERROR') || message.includes('INVALID_EMAIL_ERROR')) {
        errorMessage = t('signup.fieldError');
      }

      notifications.show({
        title: errorMessage,
        message: '',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (signupSuccess) {
    return (
      <AuthPageShell
        title={t('signup.signupSuccess')}
        subtitle={t('signup.verifyEmailHint')}
        icon={<IconCheck size={22} stroke={1.75} />}
        accentColor={accentColor}
        width="wide"
      >
        <div className={authClasses.submitWrap}>
          <AuthPrimaryButton
            accentColor={accentColor}
            onClick={() => navigate(clientRoutes.login)}
          >
            {t('signup.goToLogin')}
          </AuthPrimaryButton>
        </div>
      </AuthPageShell>
    );
  }

  const termsUrl =
    i18n.language === 'de'
      ? 'https://robotop.de/terms-of-service/'
      : 'https://robotop.de/en/terms-of-use/';
  const privacyUrl =
    i18n.language === 'de'
      ? 'https://robotop.de/datenschutzerklaerung/'
      : 'https://robotop.de/en/privacy-policy/';

  return (
    <AuthPageShell
      title={t('signup.title')}
      subtitle={t('signup.subtitle')}
      icon={<IconUserPlus size={22} stroke={1.75} />}
      accentColor={accentColor}
      width="wide"
      footer={
        <>
          <p className={authClasses.footerText}>{t('signup.signInLink')}</p>
          <AuthSecondaryButton
            onClick={() => navigate(clientRoutes.login)}
            accentColor={accentColor}
          >
            {t('signup.signInLinkAction')}
          </AuthSecondaryButton>
        </>
      }
    >
      <form onSubmit={form.onSubmit(handleSubmit)} className={authClasses.formStack}>
        <TextInput
          label={t('signup.email')}
          placeholder={t('signup.emailPlaceholder')}
          leftSection={<IconMail size={17} />}
          size="md"
          radius="md"
          styles={inputStyles}
          {...form.getInputProps('email')}
          required
        />

        <PasswordInput
          label={t('signup.password')}
          placeholder={t('signup.passwordPlaceholder')}
          leftSection={<IconLock size={17} />}
          size="md"
          radius="md"
          styles={inputStyles}
          {...form.getInputProps('password')}
          required
        />

        <PasswordInput
          label={t('signup.confirmPassword')}
          placeholder={t('signup.confirmPasswordPlaceholder')}
          leftSection={<IconLock size={17} />}
          size="md"
          radius="md"
          styles={inputStyles}
          {...form.getInputProps('confirmPassword')}
          required
        />

        <div className={authClasses.checkboxSection}>
          <Checkbox
            size="sm"
            label={
              <span className={authClasses.checkboxLabel}>
                {t('signup.termsAndConditions')}{' '}
                <a
                  href={termsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={authClasses.externalLink}
                  style={{ color: accentColor }}
                >
                  {t('signup.termsOfService')}
                </a>{' '}
                {t('signup.and')}{' '}
                <a
                  href={privacyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={authClasses.externalLink}
                  style={{ color: accentColor }}
                >
                  {t('signup.privacyPolicy')}
                </a>{' '}
                <span className={authClasses.checkboxRequired}>*</span>
              </span>
            }
            {...form.getInputProps('termsAndConditions', { type: 'checkbox' })}
          />

          <Checkbox
            size="sm"
            label={
              <span className={authClasses.checkboxLabel}>
                {t('signup.dataProcessing')}{' '}
                <span className={authClasses.checkboxRequired}>*</span>
              </span>
            }
            {...form.getInputProps('dataProcessing', { type: 'checkbox' })}
          />

          <Checkbox
            size="sm"
            label={<span className={authClasses.checkboxLabel}>{t('signup.newsletterSignup')}</span>}
            {...form.getInputProps('newsletterSignup', { type: 'checkbox' })}
          />
        </div>

        <AuthSubmitButton
          loading={isSubmitting}
          disabled={!form.values.termsAndConditions || !form.values.dataProcessing}
          accentColor={accentColor}
        >
          {t('signup.signupButton')}
        </AuthSubmitButton>
      </form>
    </AuthPageShell>
  );
}
